import { __decorate } from "tslib";
import { attr, css, nullableNumberConverter } from "@microsoft/fast-element";
import { CSSCustomPropertyBehavior, designSystemProperty, DesignSystemProvider, designSystemProvider, forcedColorsStylesheetBehavior, DesignSystemProviderTemplate as template, } from "@microsoft/fast-foundation";
import { SystemColors } from "@microsoft/fast-web-utilities";
import { fastDesignSystemDefaults } from "../fast-design-system";
import { neutralForegroundRest } from "../color";
import { DesignSystemProviderStyles as styles } from "./design-system-provider.styles";
const color = new CSSCustomPropertyBehavior("neutral-foreground-rest", neutralForegroundRest, (el) => el);
const backgroundStyles = css `
    :host {
        background-color: var(--background-color);
        color: ${color.var};
    }
`.withBehaviors(color, forcedColorsStylesheetBehavior(css `
            :host {
                background-color: ${SystemColors.ButtonFace};
                box-shadow: 0 0 0 1px ${SystemColors.CanvasText};
                color: ${SystemColors.ButtonText};
            }
        `));
/**
 * The FAST DesignSystemProvider Element. Implements {@link @microsoft/fast-foundation#DesignSystemProvider},
 * {@link @microsoft/fast-foundation#DesignSystemProviderTemplate} and {@link @microsoft/fast-components#FASTDesignSystem}
 *
 *
 * @public
 * @remarks
 * HTML Element: \<fast-design-system-provider\>
 */
let FASTDesignSystemProvider = class FASTDesignSystemProvider extends DesignSystemProvider {
    constructor() {
        super(...arguments);
        /**
         * Used to instruct the FASTDesignSystemProvider
         * that it should not set the CSS
         * background-color and color properties
         *
         * @remarks
         * HTML boolean attribute: no-paint
         */
        this.noPaint = false;
    }
    noPaintChanged() {
        if (!this.noPaint && this.backgroundColor !== void 0) {
            this.$fastController.addStyles(backgroundStyles);
        }
        else {
            this.$fastController.removeStyles(backgroundStyles);
        }
    }
    backgroundColorChanged() {
        // If background changes or is removed, we need to
        // re-evaluate whether we should have paint styles applied
        this.noPaintChanged();
    }
};
__decorate([
    attr({ attribute: "no-paint", mode: "boolean" })
], FASTDesignSystemProvider.prototype, "noPaint", void 0);
__decorate([
    designSystemProperty({
        attribute: "background-color",
        default: fastDesignSystemDefaults.backgroundColor,
    })
], FASTDesignSystemProvider.prototype, "backgroundColor", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-base-color",
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentBaseColor,
    })
], FASTDesignSystemProvider.prototype, "accentBaseColor", void 0);
__decorate([
    designSystemProperty({
        attribute: false,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralPalette,
    })
], FASTDesignSystemProvider.prototype, "neutralPalette", void 0);
__decorate([
    designSystemProperty({
        attribute: false,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentPalette,
    })
], FASTDesignSystemProvider.prototype, "accentPalette", void 0);
__decorate([
    designSystemProperty({
        default: fastDesignSystemDefaults.density,
        converter: nullableNumberConverter,
    })
], FASTDesignSystemProvider.prototype, "density", void 0);
__decorate([
    designSystemProperty({
        attribute: "design-unit",
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.designUnit,
    })
], FASTDesignSystemProvider.prototype, "designUnit", void 0);
__decorate([
    designSystemProperty({
        attribute: "direction",
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.direction,
    })
], FASTDesignSystemProvider.prototype, "direction", void 0);
__decorate([
    designSystemProperty({
        attribute: "base-height-multiplier",
        default: fastDesignSystemDefaults.baseHeightMultiplier,
        converter: nullableNumberConverter,
    })
], FASTDesignSystemProvider.prototype, "baseHeightMultiplier", void 0);
__decorate([
    designSystemProperty({
        attribute: "base-horizontal-spacing-multiplier",
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.baseHorizontalSpacingMultiplier,
    })
], FASTDesignSystemProvider.prototype, "baseHorizontalSpacingMultiplier", void 0);
__decorate([
    designSystemProperty({
        attribute: "corner-radius",
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.cornerRadius,
    })
], FASTDesignSystemProvider.prototype, "cornerRadius", void 0);
__decorate([
    designSystemProperty({
        attribute: "outline-width",
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.outlineWidth,
    })
], FASTDesignSystemProvider.prototype, "outlineWidth", void 0);
__decorate([
    designSystemProperty({
        attribute: "focus-outline-width",
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.focusOutlineWidth,
    })
], FASTDesignSystemProvider.prototype, "focusOutlineWidth", void 0);
__decorate([
    designSystemProperty({
        attribute: "disabled-opacity",
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.disabledOpacity,
    })
], FASTDesignSystemProvider.prototype, "disabledOpacity", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-minus-2-font-size",
        default: fastDesignSystemDefaults.typeRampMinus2FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampMinus2FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-minus-2-line-height",
        default: fastDesignSystemDefaults.typeRampMinus2LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampMinus2LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-minus-1-font-size",
        default: fastDesignSystemDefaults.typeRampMinus1FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampMinus1FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-minus-1-line-height",
        default: fastDesignSystemDefaults.typeRampMinus1LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampMinus1LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-base-font-size",
        default: fastDesignSystemDefaults.typeRampBaseFontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampBaseFontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-base-line-height",
        default: fastDesignSystemDefaults.typeRampBaseLineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampBaseLineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-1-font-size",
        default: fastDesignSystemDefaults.typeRampPlus1FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus1FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-1-line-height",
        default: fastDesignSystemDefaults.typeRampPlus1LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus1LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-2-font-size",
        default: fastDesignSystemDefaults.typeRampPlus2FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus2FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-2-line-height",
        default: fastDesignSystemDefaults.typeRampPlus2LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus2LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-3-font-size",
        default: fastDesignSystemDefaults.typeRampPlus3FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus3FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-3-line-height",
        default: fastDesignSystemDefaults.typeRampPlus3LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus3LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-4-font-size",
        default: fastDesignSystemDefaults.typeRampPlus4FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus4FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-4-line-height",
        default: fastDesignSystemDefaults.typeRampPlus4LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus4LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-5-font-size",
        default: fastDesignSystemDefaults.typeRampPlus5FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus5FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-5-line-height",
        default: fastDesignSystemDefaults.typeRampPlus5LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus5LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-6-font-size",
        default: fastDesignSystemDefaults.typeRampPlus6FontSize,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus6FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: "type-ramp-plus-6-line-height",
        default: fastDesignSystemDefaults.typeRampPlus6LineHeight,
    })
], FASTDesignSystemProvider.prototype, "typeRampPlus6LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-fill-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentFillRestDelta,
    })
], FASTDesignSystemProvider.prototype, "accentFillRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-fill-hover-delta",
        cssCustomProperty: false,
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.accentFillHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "accentFillHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-fill-active-delta",
        cssCustomProperty: false,
        converter: nullableNumberConverter,
        default: fastDesignSystemDefaults.accentFillActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "accentFillActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-fill-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentFillFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "accentFillFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-fill-selected-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentFillSelectedDelta,
    })
], FASTDesignSystemProvider.prototype, "accentFillSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-foreground-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentForegroundRestDelta,
    })
], FASTDesignSystemProvider.prototype, "accentForegroundRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-foreground-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentForegroundHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "accentForegroundHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-foreground-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentForegroundActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "accentForegroundActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "accent-foreground-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.accentForegroundFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "accentForegroundFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillRestDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-selected-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillSelectedDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-input-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillInputRestDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillInputRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-input-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillInputHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillInputHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-input-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillInputActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillInputActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-input-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillInputFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillInputFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-input-selected-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillInputSelectedDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillInputSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-stealth-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillStealthRestDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillStealthRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-stealth-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillStealthHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillStealthHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-stealth-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillStealthActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillStealthActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-stealth-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillStealthFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillStealthFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-stealth-selected-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillStealthSelectedDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillStealthSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-toggle-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillToggleHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillToggleHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-toggle-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillToggleActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillToggleActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-toggle-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillToggleFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillToggleFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "base-layer-luminance",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.baseLayerLuminance,
    })
], FASTDesignSystemProvider.prototype, "baseLayerLuminance", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-fill-card-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralFillCardDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralFillCardDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-foreground-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralForegroundHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralForegroundHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-foreground-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralForegroundActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralForegroundActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-foreground-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralForegroundFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralForegroundFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-divider-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralDividerRestDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralDividerRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-outline-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralOutlineRestDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralOutlineRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-outline-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralOutlineHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralOutlineHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-outline-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralOutlineActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralOutlineActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-outline-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralOutlineFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralOutlineFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-contrast-fill-rest-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralContrastFillRestDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralContrastFillRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-contrast-fill-hover-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralContrastFillHoverDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralContrastFillHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-contrast-fill-active-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralContrastFillActiveDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralContrastFillActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: "neutral-contrast-fill-focus-delta",
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: fastDesignSystemDefaults.neutralContrastFillFocusDelta,
    })
], FASTDesignSystemProvider.prototype, "neutralContrastFillFocusDelta", void 0);
FASTDesignSystemProvider = __decorate([
    designSystemProvider({
        name: "fast-design-system-provider",
        template,
        styles,
    })
], FASTDesignSystemProvider);
export { FASTDesignSystemProvider };
